/**
 * Tasks tab for single-order view.
 *
 * The left task list loads from `GET /oncoemr-tasks/by-order/{order_id}`. The
 * conversation panel loads `GET /oncoemr-tasks/{task_row_id}/messages`, queues replies with
 * `POST /oncoemr-tasks/{task_row_id}/reply`, retries failed replies with
 * `POST /oncoemr-tasks/replies/{idempotency_key}/retry`, and acks with
 * `POST /oncoemr-tasks/{task_row_id}/ack`
 * when an updated thread is viewed. Link-task flow
 * remains UI-only until its API exists.
 *
 * Tab shell uses a viewport-based **fixed height** (`calc(100dvh - …)`) so nested
 * `overflow-y-auto` regions get a definite height (max-height alone stayed content-sized).
 * @module tasks
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import { createOncoemrTask } from "../../../../api/postCall/createOncoemrTask";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { useTasksNotification } from "../../../../context/tasksNotification/tasksNotificationContext";
import { useOncoemrTasksEnabled } from "../../../../hooks/useOncoemrTasksEnabled";
import { RootState } from "../../../../redux/store/store";
import RedWarningIcon from "../../../../svg/redWarningIcon";
import { formatOncoemrTaskInitialMessage } from "../../../../utils/oncoemrTaskHelpers";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import CreateTaskModal, {
  CreateTaskFormData,
} from "./components/createTaskModal";
import LinkTaskConfirmModal from "./components/linkTaskConfirmModal";
import LinkTaskSelectModal from "./components/linkTaskSelectModal";
import type { LinkTaskCandidate } from "./components/linkTaskTypes";
import TasksConversationPanel from "./components/tasksConversationPanel";
import TasksListPanel from "./components/tasksListPanel";
import { useOncoemrTaskAck } from "./hooks/useOncoemrTaskAck";
import { useOncoemrTaskReply } from "./hooks/useOncoemrTaskReply";
import { useOncoemrTaskReplyRetry } from "./hooks/useOncoemrTaskReplyRetry";
import { useOncoemrTasksList } from "./hooks/useOncoemrTasksList";
import { useTaskConversationView } from "./hooks/useTaskConversationView";

/**
 * Renders the single-order Tasks tab: API-backed list plus conversation panel,
 * or empty state with create/link actions when there are no tasks for the case.
 *
 * @returns Tasks layout or empty-state view for task management actions.
 */
const Tasks = () => {
  const isOncoemrTasksEnabled = useOncoemrTasksEnabled();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { applyTaskAck: applyGlobalTaskAck } = useTasksNotification();
  const orgId = getOrgIdForFetchExternalWorklist() ?? "";
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const order = generalOrderDetails?.order ?? null;
  const [isLinkTaskSelectModalOpen, setIsLinkTaskSelectModalOpen] =
    useState(false);
  const [isLinkTaskConfirmModalOpen, setIsLinkTaskConfirmModalOpen] =
    useState(false);
  const [pendingLinkTask, setPendingLinkTask] = useState<
    LinkTaskCandidate | undefined
  >(undefined);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>();
  const deepLinkTaskIdConsumedRef = useRef(false);

  const {
    panelItems,
    tasksForOrder,
    loading: tasksLoading,
    refetch,
    applyTaskAck,
  } = useOncoemrTasksList({
    orgId,
    orderId: orderId ?? "",
  });

  /**
   * Submits the create-task form to `POST /oncoemr-tasks`.
   *
   * @param formData - Modal fields mapped to title, initial message, and recipients.
   * @throws Error when required order context or the API call fails.
   */
  const handleCreateTask = useCallback(
    async (formData: CreateTaskFormData) => {
      const mrn = order?.demographics?.patient_id ?? "";

      if (!orgId || !user?.id || !orderId || !mrn) {
        toast.error(ToastMessages.TASK_CREATE_MISSING_CONTEXT);
        throw new Error(ToastMessages.TASK_CREATE_MISSING_CONTEXT);
      }

      setIsCreatingTask(true);

      try {
        await createOncoemrTask({
          org_id: orgId,
          user_id: user.id,
          patient_id: mrn,
          title: formData.taskName.trim(),
          initial_message: formatOncoemrTaskInitialMessage(formData.taskNotes),
          recipient_ids: formData.recipientIds,
        });
        toast.success(ToastMessages.TASK_CREATE_SUCCESS);
        refetch();
      } catch (error) {
        toast.error(ToastMessages.TASK_CREATE_FAILURE);
        throw error instanceof Error
          ? error
          : new Error(ToastMessages.TASK_CREATE_FAILURE);
      } finally {
        setIsCreatingTask(false);
      }
    },
    [order?.demographics?.patient_id, orderId, orgId, refetch, user?.id],
  );

  /**
   * Apply `?taskId=` once when the list contains that row, then keep selection in
   * sync with `panelItems` without re-reading the URL (so clicks are not clobbered).
   */
  useEffect(() => {
    if (!deepLinkTaskIdConsumedRef.current) {
      const taskIdFromUrl = searchParams.get("taskId");
      if (taskIdFromUrl) {
        if (panelItems.length === 0) {
          if (tasksLoading) {
            return;
          }
          deepLinkTaskIdConsumedRef.current = true;
        } else {
          deepLinkTaskIdConsumedRef.current = true;
          if (panelItems.some((task) => task.id === taskIdFromUrl)) {
            setSelectedTaskId(taskIdFromUrl);
            return;
          }
        }
      } else {
        deepLinkTaskIdConsumedRef.current = true;
      }
    }

    if (panelItems.length === 0) {
      setSelectedTaskId(undefined);
      return;
    }

    setSelectedTaskId((current) => {
      if (current && panelItems.some((task) => task.id === current)) {
        return current;
      }
      return panelItems[0].id;
    });
  }, [panelItems, searchParams, tasksLoading]);

  const selectedPanelItem = useMemo(
    () => panelItems.find((task) => task.id === selectedTaskId),
    [panelItems, selectedTaskId],
  );

  const selectedTaskRow = useMemo(
    () => tasksForOrder.find((task) => task.id === selectedTaskId),
    [tasksForOrder, selectedTaskId],
  );

  const { conversation: selectedTaskConversation, refetchMessages } =
    useTaskConversationView({
      panelItem: selectedPanelItem,
      taskRowId: selectedTaskId,
      orgId,
    });

  const handleReplyQueued = useCallback(async () => {
    await refetchMessages();
    void refetch();
  }, [refetchMessages, refetch]);

  const { submitReply, isSubmitting } = useOncoemrTaskReply({
    orgId,
    taskRowId: selectedTaskId,
    userId: user?.id,
    onReplyQueued: handleReplyQueued,
  });

  const { retryFailedReply } = useOncoemrTaskReplyRetry({
    orgId,
    onReplyRetried: handleReplyQueued,
  });

  /**
   * Queues an outbound message for the selected task and surfaces API feedback.
   *
   * @param body - Plain-text message from the conversation composer.
   */
  const handleSendTaskReply = useCallback(
    async (body: string) => {
      if (!orgId || !user?.id || !selectedTaskId) {
        toast.error(ToastMessages.TASK_REPLY_MISSING_CONTEXT);
        throw new Error(ToastMessages.TASK_REPLY_MISSING_CONTEXT);
      }

      try {
        await submitReply(body);
      } catch (error) {
        toast.error(ToastMessages.TASK_REPLY_FAILURE);
        throw error instanceof Error
          ? error
          : new Error(ToastMessages.TASK_REPLY_FAILURE);
      }
    },
    [orgId, selectedTaskId, submitReply, user?.id],
  );

  /**
   * Re-queues a failed outbound message and refreshes the thread from API status.
   *
   * @param idempotencyKey - Outbox row key from the failed message bubble.
   */
  const handleRetryFailedReply = useCallback(
    async (idempotencyKey: string) => {
      if (!orgId || !idempotencyKey) {
        toast.error(ToastMessages.TASK_REPLY_RETRY_MISSING_CONTEXT);
        return;
      }

      try {
        const result = await retryFailedReply(idempotencyKey);
        toast.success(result.message);
      } catch (error) {
        const failureMessage =
          error instanceof Error && error.message.trim()
            ? error.message
            : ToastMessages.TASK_REPLY_RETRY_FAILURE;
        toast.error(failureMessage);
      }
    },
    [orgId, retryFailedReply],
  );

  const handleTaskAcknowledged = useCallback(
    (taskRowId: string, version: number) => {
      applyTaskAck(taskRowId, version);
      applyGlobalTaskAck(taskRowId, version);
    },
    [applyTaskAck, applyGlobalTaskAck],
  );

  const handleRefetchTasksForAck = useCallback(() => {
    void refetch();
  }, [refetch]);

  const messagesReady = Boolean(
    selectedTaskConversation &&
    !selectedTaskConversation.messagesLoading &&
    !selectedTaskConversation.messagesError,
  );

  useOncoemrTaskAck({
    orgId,
    taskRowId: selectedTaskId,
    expectedVersion: selectedTaskRow?.version,
    isUpdated: selectedTaskRow?.is_updated,
    messagesReady,
    onAcknowledged: handleTaskAcknowledged,
    onRefetchTasks: handleRefetchTasksForAck,
  });

  const showTaskWorkspace = tasksLoading || panelItems.length > 0;

  if (!isOncoemrTasksEnabled) {
    return <Navigate to="../cptCodes" replace />;
  }

  return (
    <>
      <div className="flex h-[max(12rem,calc(100dvh-16rem))] min-h-0 w-full flex-col gap-2 overflow-hidden">
        {showTaskWorkspace ? (
          <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-primaryGray-14">
            <TasksListPanel
              tasks={panelItems}
              selectedTaskId={selectedTaskId}
              isLoading={tasksLoading}
              onTaskSelect={setSelectedTaskId}
              onCreateTask={() => setIsCreateTaskModalOpen(true)}
              onLinkTask={() => setIsLinkTaskSelectModalOpen(true)}
            />
            <TasksConversationPanel
              taskConversation={selectedTaskConversation}
              onSendMessage={handleSendTaskReply}
              isSendingMessage={isSubmitting}
              onRetryFailedReply={handleRetryFailedReply}
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-5 px-4 py-6">
              <div className="relative flex h-[17rem] w-[18.25rem] items-center justify-center">
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.45]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, rgb(214 214 214 / 0.45) 1px, transparent 1px), linear-gradient(to bottom, rgb(214 214 214 / 0.45) 1px, transparent 1px)",
                    backgroundSize: "32px 32px, 32px 32px",
                    width: "18.25rem",
                    height: "17rem",
                  }}
                  aria-hidden
                />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-sm border border-[#2E2E2E] bg-white shadow-[3px_3px_0_0_#2E2E2E]">
                  <RedWarningIcon width={25} height={25} />
                </div>
              </div>
              <p className="text-center text-[0.875rem] font-normal not-italic leading-normal text-primaryGray-3">
                No Tasks exist for this case
              </p>
              <div className="flex items-start gap-3">
                <Button
                  buttonType="secondary"
                  size="medium"
                  disabled={false}
                  onClick={() => setIsLinkTaskSelectModalOpen(true)}
                >
                  Link Task
                </Button>
                <Button
                  buttonType="primary"
                  size="medium"
                  disabled={false}
                  onClick={() => setIsCreateTaskModalOpen(true)}
                >
                  Create Task
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {isCreateTaskModalOpen && (
        <CreateTaskModal
          onClose={() => setIsCreateTaskModalOpen(false)}
          onSubmit={handleCreateTask}
          isSubmitting={isCreatingTask}
        />
      )}
      {isLinkTaskSelectModalOpen && (
        <LinkTaskSelectModal
          candidates={[]}
          onClose={() => setIsLinkTaskSelectModalOpen(false)}
          onProceedToConfirm={(task) => {
            setPendingLinkTask(task);
            setIsLinkTaskSelectModalOpen(false);
            setIsLinkTaskConfirmModalOpen(true);
          }}
        />
      )}
      {isLinkTaskConfirmModalOpen && pendingLinkTask && (
        <LinkTaskConfirmModal
          task={pendingLinkTask}
          onClose={() => {
            setIsLinkTaskConfirmModalOpen(false);
            setPendingLinkTask(undefined);
          }}
          onLink={(_task) => {
            // TODO: call link-task API with selected task id.
          }}
        />
      )}
    </>
  );
};

export default Tasks;
